import SpintrAPI from "src/spintr/SpintrApi";
import type {
    AppDetail,
    AppListParams,
    AppListResponse,
    AiModelList,
    AppFileTree,
    AppSecretSummary,
    AppVersionListResponse,
    ConversationHistoryPage,
    ExecuteActionResponse,
    StudioAppBrief,
    StudioAppCounts,
    StudioAppUpdateRequest,
    StudioCurrentUser,
    StudioReviewInsights,
    StudioAppTypeValue,
    StudioForkResult,
    StudioSettings,
    StudioSettingsUpdateRequest,
    StudioStreamingActive,
    StudioWidgetData,
    StoreListResponse,
} from "src/studio/types";

export const studioApi = {
    getCurrentUser: () =>
        SpintrAPI.get<StudioCurrentUser>(`/api/v1/StudioAppBridge/me`).then(r => r.data),

    list: (params?: AppListParams) =>
        SpintrAPI.get<AppListResponse>(`/api/v1/studioapps`, { params }).then(r => r.data),

    getAppCounts: (params?: { hideDeleted?: boolean }) =>
        SpintrAPI.get<StudioAppCounts>(`/api/v1/studioapps/counts`, { params }).then(r => r.data),

    listStoreApps: (params?: { skip?: number; take?: number; searchText?: string; type?: StudioAppTypeValue }) =>
        SpintrAPI.get<StoreListResponse>(`/api/v1/studioapps/store`, { params }).then(r => r.data),

    // Fork a public store app into the current instance; returns the new, editable app.
    installStoreApp: (appId: number) =>
        SpintrAPI.post<StudioForkResult>(`/api/v1/studioapps/${appId}/install`, {}).then(r => r.data),

    // Duplicate one of the current instance's own apps into a new variant.
    duplicateApp: (appId: number) =>
        SpintrAPI.post<StudioForkResult>(`/api/v1/studioapps/${appId}/duplicate`, {}).then(r => r.data),

    get: (appId: number, versionId?: number) =>
        SpintrAPI.get<AppDetail>(
            versionId
                ? `/api/v1/studioapps/${appId}/versions/${versionId}`
                : `/api/v1/studioapps/${appId}`
        ).then(r => r.data),

    getPublished: (appId: number) =>
        SpintrAPI.get<AppDetail>(`/api/v1/studioapps/${appId}/published`).then(r => r.data),

    getReviewInsights: (appId: number, generate: boolean = true) =>
        SpintrAPI.get<StudioReviewInsights>(`/api/v1/studioapps/${appId}/review-insights`, { params: { generate } }).then(r => r.data),

    getWidgetData: (appId: number) =>
        SpintrAPI.get<StudioWidgetData>(`/api/v1/studioapps/${appId}/data`).then(r => r.data),

    listVersions: (appId: number, skip: number = 0, take: number = 10) =>
        SpintrAPI.get<AppVersionListResponse>(`/api/v1/studioapps/${appId}/versions`, {
            params: { skip, take },
        }).then(r => r.data),

    revertVersion: (appId: number, versionId: number) =>
        SpintrAPI.post<AppDetail>(
            `/api/v1/studioapps/${appId}/versions/${versionId}/revert`,
            {}
        ).then(r => r.data),

    listSecrets: (appId: number) =>
        SpintrAPI.get<AppSecretSummary[]>(`/api/v1/studioapps/${appId}/secrets`).then(r => r.data),

    upsertSecret: (appId: number, key: string, value: string) =>
        SpintrAPI.put<AppSecretSummary>(
            `/api/v1/studioapps/${appId}/secrets/${encodeURIComponent(key)}`,
            { Value: value }
        ).then(r => r.data),

    conversation: (appId: number, params?: { beforeOrderIndex?: number; take?: number }) =>
        SpintrAPI.get<ConversationHistoryPage>(`/api/v1/studioconversations/${appId}`, { params }).then(r => r.data),

    getActiveStream: (appId: number) =>
        SpintrAPI.get<StudioStreamingActive | null>(`/api/v1/studioconversations/${appId}/streaming`).then(r => r.data),

    // Persists the clean build; the RETURNED bundle is instrumented (inline-edit data-* attributes)
    // when available so the workbench preview can offer edit mode without a rebuild.
    bundle: (versionId: number, fileTree: AppFileTree) =>
        SpintrAPI.put<{ success: boolean; iife?: string; css?: string; error?: string; instrumented?: boolean }>(
            `/api/v1/studioapps/versions/${versionId}/bundle`,
            { fileTree }
        ).then(r => r.data),

    // Instrumented build (carries inline-edit data-* attributes) for the editable preview. Not persisted.
    previewBundle: (versionId: number) =>
        SpintrAPI.post<{ success: boolean; iife?: string; css?: string; error?: string; instrumented?: boolean }>(
            `/api/v1/studioapps/versions/${versionId}/preview-bundle`,
            {}
        ).then(r => r.data),

    // Replace one static text node located by tagger coordinates; returns the (possibly new draft)
    // version plus a fresh instrumented bundle so the editable preview keeps valid coordinates.
    inlineTextEdit: (
        versionId: number,
        edit: { filePath: string; line: number; column: number; newText: string },
    ) =>
        SpintrAPI.post<{
            versionId: number;
            version: number;
            isPublished: boolean;
            iife?: string;
            css?: string;
            cleanIife?: string;
            cleanCss?: string;
            filePath?: string;
            fileContent?: string;
        }>(
            `/api/v1/studioapps/versions/${versionId}/inline-text`,
            edit
        ).then(r => r.data),

    executeAction: (versionId: number, actionName: string, parameters: Record<string, unknown>) =>
        SpintrAPI.post<ExecuteActionResponse>(
            `/api/v1/studioapps/versions/${versionId}/actions/${actionName}/execute`,
            { parameters }
        ).then(r => r.data),

    saveFiles: (versionId: number, fileTree: AppFileTree) =>
        SpintrAPI.put<{ versionId: number; version: number; isPublished: boolean }>(
            `/api/v1/studioapps/versions/${versionId}/files`,
            { fileTree }
        ).then(r => r.data),

    publish: (appId: number) =>
        SpintrAPI.post<{ appId: number; publishedVersion: number; publishedVersionId: number }>(
            `/api/v1/studioapps/${appId}/publish`,
            {}
        ).then(r => r.data),

    update: (appId: number, update: StudioAppUpdateRequest) =>
        SpintrAPI.put<{ appId: number; name: string }>(
            `/api/v1/studioapps/${appId}`,
            update
        ).then(r => r.data),

    toggleDelete: (appId: number) =>
        SpintrAPI.delete<{ appId: number; isDeleted: boolean }>(`/api/v1/studioapps/${appId}`).then(r => r.data),

    models: () =>
        SpintrAPI.get<AiModelList>(`/api/v1/studiosettings/models`).then(r => r.data),

    getSettings: () =>
        SpintrAPI.get<StudioSettings>(`/api/v1/studiosettings`).then(r => r.data),

    updateSettings: (update: StudioSettingsUpdateRequest) =>
        SpintrAPI.put<StudioSettings>(`/api/v1/studiosettings`, update).then(r => r.data),

    draftCustomerContext: () =>
        SpintrAPI.post<{ text: string }>(`/api/v1/studiosettings/customer-context/draft`, {}).then(r => r.data),

    getBrief: (appId: number) =>
        SpintrAPI.get<StudioAppBrief>(`/api/v1/studioapps/${appId}/brief`).then(r => r.data),

    updateBrief: (appId: number, brief: string) =>
        SpintrAPI.put<StudioAppBrief>(`/api/v1/studioapps/${appId}/brief`, { Brief: brief }).then(r => r.data),
};
